"use client"

import { useState, useEffect } from "react"
import { Layout, Menu, Avatar, Button, Tooltip } from "antd"
import { UserOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import styles from "./style.module.scss"
import { useMenuData } from "../../menuData"

const { Sider } = Layout

const SidebarOfficial = ({ collapsed, toggleCollapsed }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const menuData = useMenuData()
  const [selectedKeys, setSelectedKeys] = useState(["dashboard"])

  // Update selected keys based on current route
  useEffect(() => {
    const currentPath = location.pathname
    const findSelectedKey = (items) => {
      for (const item of items) {
        if (item.to === currentPath) {
          return item.key
        }
        if (item.children) {
          const childKey = findSelectedKey(item.children)
          if (childKey) return childKey
        }
      }
      return null
    }

    const key = findSelectedKey(menuData)
    if (key) {
      setSelectedKeys([key])
    }
  }, [location.pathname, menuData])

  const handleLogout = () => {
    logout()
  }

  const renderMenuItem = (item) => (
    <Menu.Item key={item.key} icon={<span className={styles.menuIcon}>{item.icon}</span>} className={styles.menuItem}>
      {item.to ? (
        <Link to={item.to} className={styles.menuLink}>
          {item.title}
        </Link>
      ) : (
        <span className={styles.menuLink}>{item.title}</span>
      )}
    </Menu.Item>
  )

  const renderSubMenu = (submenu) => {
    const visibleChildren = submenu.children.filter((child) => child.isVisible)

    if (visibleChildren.length === 0) {
      return null
    }

    return (
      <Menu.SubMenu
        key={submenu.key}
        icon={<span className={styles.menuIcon}>{submenu.icon}</span>}
        title={<span className={styles.submenuTitle}>{submenu.title}</span>}
        className={styles.submenu}
      >
        {visibleChildren.map((child) => renderMenuItem(child))}
      </Menu.SubMenu>
    )
  }

  const renderMenuItems = () => {
    const filteredItems = menuData.filter((item) => item.isVisible)

    return filteredItems.flatMap((item, index) => {
      const menuItem = item.children ? renderSubMenu(item) : renderMenuItem(item)
      const divider = item.hasDividerBefore ? <div key={`divider-${index}`} className={styles.menuDivider} /> : null

      return divider ? [divider, menuItem] : menuItem
    })
  }

  return (
    <Sider
      width={280}
      collapsible
      reverseArrow={false}
      collapsed={collapsed}
      onCollapse={toggleCollapsed}
      breakpoint="lg"
      collapsedWidth={80}
      theme="light"
      className={styles.sidebar}
      trigger={null}
    >
      {/* Header */}
      <div className={styles.sidebarHeader}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            <UserOutlined />
          </div>
          {!collapsed && (
            <div className={styles.logoText}>
              <h3>Dashboard</h3>
              <span>Admin Panel</span>
            </div>
          )}
        </div>

        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggleCollapsed}
          className={styles.collapseButton}
        />
      </div>

      {/* User Info */}
      {user && (
        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <Avatar size={collapsed ? 32 : 48} icon={<UserOutlined />} className={styles.userAvatar} />
            {!collapsed && (
              <div className={styles.userDetails}>
                <h4>{user.nome || "Usuário"}</h4>
                <span>{user.email || "user@example.com"}</span>
                <div className={styles.userBadge}>{user.tipoUsuario === 1 ? "Admin" : "Usuário"}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu */}
      <div className={styles.menuContainer}>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={selectedKeys}
          className={styles.menu}
          inlineIndent={collapsed ? 0 : 24}
        >
          {renderMenuItems()}
        </Menu>
      </div>

      {/* Footer */}
      <div className={styles.sidebarFooter}>
        <Tooltip title={collapsed ? "Sair da conta" : ""} placement="right">
          <Button icon={<LogoutOutlined />} type="text" onClick={handleLogout} className={styles.logoutButton} block>
            {!collapsed && "Sair da conta"}
          </Button>
        </Tooltip>
      </div>
    </Sider>
  )
}

export default SidebarOfficial
